// 디렉토리 경로: client/src/components/management/
// 파일명: CollectionView.tsx
// 파일 코드의 역할/설명: 스크립트 모음 뷰 컴포넌트 (좌 회차 인덱스 + 우 연속 리더, 완성 토글, 검색 및 복사 기능)

import React, { useState, useMemo, useCallback } from 'react'
import type { Script } from '@/types'
import { useAppStore } from '@/stores'
import styles from './CollectionView.module.css'

// ============================================================================
// Types
// ============================================================================
export interface CollectionViewProps {
  scripts: Script[]
  editScript: (id: string, updates: Partial<Script>) => Promise<boolean>
  onOpenInEditor: (script: Script) => void
}

interface SeriesGroup {
  seriesId: string
  title: string
  /** 그룹 출처: 연재(series) / 카테고리 폴백(category) / 미분류(none) */
  kind: 'series' | 'category' | 'none'
  scripts: Script[]
}

// ============================================================================
// Component
// ============================================================================
export function CollectionView({
  scripts,
  editScript,
  onOpenInEditor,
}: CollectionViewProps) {
  // ========================================================================
  // Store (Toast)
  // ========================================================================
  const showToast = useAppStore((state) => state.showToast)

  // ========================================================================
  // Local State (선택 ID 하나만 유지, 객체 복제 state 금지)
  // ========================================================================
  const [selectedScriptId, setSelectedScriptId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [completedFilter, setCompletedFilter] = useState<'all' | 'completed'>('all')
  const [mobileView, setMobileView] = useState<'index' | 'reader'>('index')

  // ========================================================================
  // 필터링 및 그룹화/정렬 로직
  // ========================================================================
  const { filteredList, groupedSeries, totalCount, completedCount } = useMemo(() => {
    let list = [...scripts]

    // 1. 검색어 필터
    const query = searchQuery.trim().toLowerCase()
    if (query) {
      list = list.filter((s) => {
        const nameMatch = s.name.toLowerCase().includes(query)
        const contentMatch = s.content.toLowerCase().includes(query)
        const seriesMatch = s.seriesId?.toLowerCase().includes(query) ?? false
        const categoryMatch = s.category?.toLowerCase().includes(query) ?? false
        return nameMatch || contentMatch || seriesMatch || categoryMatch
      })
    }

    // 2. 완성 여부 필터
    if (completedFilter === 'completed') {
      list = list.filter((s) => Boolean(s.completedAt))
    }

    // 3. 시리즈별 그룹화 및 정렬
    const groupMap = new Map<string, Script[]>()

    list.forEach((s) => {
      // [P2 리뷰 반영] 출처를 키에 네임스페이스 — 동명 연재/카테고리 충돌 방지
      const series = s.seriesId?.trim()
      const category = s.category?.trim()
      const key = series ? `S:${series}` : category ? `C:${category}` : ''
      const existing = groupMap.get(key) || []
      existing.push(s)
      groupMap.set(key, existing)
    })

    // 그룹 내 정렬: episode ?? Infinity 오름차순, 동률·미지정은 createdAt 오름차순
    const sortFn = (a: Script, b: Script) => {
      const epA = a.episode ?? Infinity
      const epB = b.episode ?? Infinity
      if (epA !== epB) {
        return epA - epB
      }
      const timeA = new Date(a.createdAt).getTime()
      const timeB = new Date(b.createdAt).getTime()
      return timeA - timeB
    }

    const groups: SeriesGroup[] = []
    const sortedKeys = Array.from(groupMap.keys()).sort((a, b) => {
      // '미분류'('')은 항상 맨 뒤로
      if (!a && b) return 1
      if (a && !b) return -1
      return a.localeCompare(b)
    })

    const allOrderedScripts: Script[] = []

    sortedKeys.forEach((key) => {
      const sortedScripts = groupMap.get(key)!.sort(sortFn)
      const kind: SeriesGroup['kind'] = key.startsWith('S:') ? 'series' : key.startsWith('C:') ? 'category' : 'none'
      groups.push({
        seriesId: key,
        title: key ? key.slice(2) : '미분류',
        kind,
        scripts: sortedScripts,
      })
      allOrderedScripts.push(...sortedScripts)
    })

    const total = scripts.length
    const completed = scripts.filter((s) => Boolean(s.completedAt)).length

    return {
      filteredList: allOrderedScripts,
      groupedSeries: groups,
      totalCount: total,
      completedCount: completed,
    }
  }, [scripts, searchQuery, completedFilter])

  // ========================================================================
  // 핸들러: 인덱스 행 클릭 -> 리더 스크롤
  // ========================================================================
  const handleSelectEpisode = useCallback((scriptId: string) => {
    setSelectedScriptId(scriptId)
    // 리더 뷰의 해당 에피소드로 부드럽게 스크롤
    const targetElement = document.getElementById(`reader-episode-${scriptId}`)
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    // 모바일에서는 리더 탭으로 전환
    setMobileView('reader')
  }, [])

  // ========================================================================
  // 핸들러: 완성 토글 (인라인)
  // ========================================================================
  const handleToggleComplete = useCallback(
    async (script: Script, e: React.MouseEvent) => {
      e.stopPropagation()
      try {
        const isCurrentlyCompleted = Boolean(script.completedAt)
        const success = await editScript(script.id, {
          completedAt: isCurrentlyCompleted ? undefined : new Date(),
        })
        if (!success) {
          showToast('완성 상태 변경에 실패했습니다.', 'error')
        }
      } catch (err) {
        console.error('[CollectionView] 완성 상태 변경 실패:', err)
        showToast('완성 상태 변경에 실패했습니다.', 'error')
      }
    },
    [editScript, showToast]
  )

  // ========================================================================
  // 핸들러: 클립보드 복사 (useAppStore showToast 사용 - alert 금지)
  // ========================================================================
  const handleCopyContent = useCallback(
    async (content: string) => {
      try {
        await navigator.clipboard.writeText(content)
        showToast('스크립트 전문이 클립보드에 복사되었습니다.', 'success')
      } catch (err) {
        console.error('[CollectionView] 복사 실패:', err)
        showToast('복사에 실패했습니다.', 'error')
      }
    },
    [showToast]
  )

  // ========================================================================
  // 핸들러: 필터 초기화
  // ========================================================================
  const handleResetFilter = useCallback(() => {
    setSearchQuery('')
    setCompletedFilter('all')
  }, [])

  // ========================================================================
  // Render
  // ========================================================================
  return (
    <div className={styles.collectionViewContainer}>
      {/* ================================================================
          상단 필터 바
          ================================================================ */}
      <div className={styles.collectionFilterBar}>
        <div className={styles.filterControls}>
          {/* 검색 입력 */}
          <input
            type="text"
            className={styles.searchInput}
            placeholder="스크립트 이름, 내용, 연재 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* 칩 필터 (기본: 전체) */}
          <div className={styles.chipGroup} role="group" aria-label="완성 필터">
            <button
              type="button"
              className={`${styles.chipBtn} ${
                completedFilter === 'all' ? styles.activeChip : ''
              }`}
              onClick={() => setCompletedFilter('all')}
            >
              전체 ({totalCount})
            </button>
            <button
              type="button"
              className={`${styles.chipBtn} ${
                completedFilter === 'completed' ? styles.activeChip : ''
              }`}
              onClick={() => setCompletedFilter('completed')}
            >
              ✅ 완성만 ({completedCount})
            </button>
          </div>

          {/* 필터 초기화 버튼 */}
          {(searchQuery || completedFilter !== 'all') && (
            <button
              type="button"
              className={styles.resetBtn}
              onClick={handleResetFilter}
            >
              초기화
            </button>
          )}
        </div>

        {/* 통계 요약 텍스트 */}
        <div className={styles.statsText}>
          {filteredList.length}편 표시 중
        </div>

        {/* 모바일 배타 전환 탭 (max-width: 768px 전용) */}
        <div className={styles.mobileSegment} role="tablist" aria-label="모음 보기 모드">
          <button
            type="button"
            role="tab"
            aria-selected={mobileView === 'index'}
            className={`${styles.mobileSegmentBtn} ${
              mobileView === 'index' ? styles.mobileSegmentActive : ''
            }`}
            onClick={() => setMobileView('index')}
          >
            📋 회차 목록 ({filteredList.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mobileView === 'reader'}
            className={`${styles.mobileSegmentBtn} ${
              mobileView === 'reader' ? styles.mobileSegmentActive : ''
            }`}
            onClick={() => setMobileView('reader')}
          >
            📖 연속 리더
          </button>
        </div>
      </div>

      {/* ================================================================
          본문 레이아웃: 좌 인덱스 / 우 연속 리더
          ================================================================ */}
      {filteredList.length === 0 ? (
        <div className={styles.emptyPlaceholder}>
          <span>📭</span>
          <p>표시할 스크립트가 없습니다.</p>
          {(searchQuery || completedFilter !== 'all') && (
            <button
              type="button"
              className={styles.resetBtn}
              onClick={handleResetFilter}
            >
              필터 초기화
            </button>
          )}
        </div>
      ) : (
        <div className={styles.collectionLayout}>
          {/* ============================================================
              좌측: 회차 인덱스 (Index Section)
              ============================================================ */}
          <aside
            className={`${styles.indexSection} ${
              mobileView === 'index' ? styles.mobileActive : ''
            }`}
            aria-label="회차 인덱스"
          >
            {groupedSeries.map((group) => (
              <div key={group.seriesId || 'no-series'} className={styles.seriesGroup}>
                {/* 연재 그룹 헤더 */}
                <div className={styles.seriesGroupHeader}>
                  <span className={styles.seriesTitle}>
                    {group.kind === 'series' ? `📖 ${group.title}` : `📁 ${group.title}`}
                  </span>
                  <span className={styles.seriesCount}>
                    {group.scripts.length}편
                  </span>
                </div>

                {/* 회차 행 목록 */}
                {group.scripts.map((script) => {
                  const isSelected = selectedScriptId === script.id
                  const isCompleted = Boolean(script.completedAt)
                  const previewText = script.content.trim() || '(내용 없음)'

                  return (
                    <div
                      key={script.id}
                      className={`${styles.indexRow} ${
                        isSelected ? styles.selectedRow : ''
                      }`}
                      onClick={() => handleSelectEpisode(script.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleSelectEpisode(script.id)
                        }
                      }}
                    >
                      {/* 행 헤더: 회차 번호 + 이름 */}
                      <div className={styles.indexRowHeader}>
                        {typeof script.episode === 'number' ? (
                          <span className={styles.episodeBadge}>
                            {script.episode}화
                          </span>
                        ) : (
                          <span className={styles.noEpisodeBadge}>
                            -
                          </span>
                        )}
                        <span className={styles.indexRowTitle} title={script.name}>
                          {script.name}
                        </span>
                      </div>

                      {/* 미리보기 (클램프 2줄 고정 높이) */}
                      <div className={styles.indexRowPreview}>
                        {previewText}
                      </div>

                      {/* 하단 메타: 글자수 + 완성 인라인 토글 */}
                      <div className={styles.indexRowFooter}>
                        <span className={styles.charCount}>
                          {script.content.length.toLocaleString()}자
                        </span>

                        <button
                          type="button"
                          className={`${styles.completeToggleBtn} ${
                            isCompleted ? styles.isCompleted : ''
                          }`}
                          onClick={(e) => handleToggleComplete(script, e)}
                          title={isCompleted ? '완성 해제하기' : '완성으로 표시'}
                          aria-label={isCompleted ? '완성 해제' : '완성 처리'}
                        >
                          {isCompleted ? '✅ 완성' : '미완성'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
          </aside>

          {/* ============================================================
              우측: 연속 리더 (Continuous Reader Section)
              ============================================================ */}
          <main
            className={`${styles.readerSection} ${
              mobileView === 'reader' ? styles.mobileActive : ''
            }`}
            aria-label="연속 리더"
          >
            {filteredList.map((script) => {
              const isCompleted = Boolean(script.completedAt)

              return (
                <article
                  key={script.id}
                  id={`reader-episode-${script.id}`}
                  className={styles.readerEpisode}
                >
                  {/* 회차 헤더 (Sticky) */}
                  <header className={styles.readerStickyHeader}>
                    <div className={styles.readerHeaderTitle}>
                      {script.seriesId && (
                        <span className={styles.readerSeriesTag}>
                          📖 {script.seriesId}
                        </span>
                      )}
                      {typeof script.episode === 'number' && (
                        <span className={styles.readerEpisodeTag}>
                          {script.episode}화
                        </span>
                      )}
                      <h4 className={styles.readerTitle}>
                        {script.name}
                      </h4>
                      {isCompleted && (
                        <span className={styles.readerCompletedTag}>
                          ✅ 완성
                        </span>
                      )}
                    </div>

                    <div className={styles.readerActions}>
                      <button
                        type="button"
                        className={styles.readerActionBtn}
                        onClick={() => handleCopyContent(script.content)}
                        title="전문 클립보드 복사"
                      >
                        📋 복사
                      </button>
                      <button
                        type="button"
                        className={styles.readerActionBtn}
                        onClick={() => onOpenInEditor(script)}
                        title="작성 모드에서 편집하기"
                      >
                        ✏️ 작성 모드로 열기
                      </button>
                    </div>
                  </header>

                  {/* 스크립트 전문 (React 텍스트 노드로만 렌더, dangerouslySetInnerHTML 금지) */}
                  <div className={styles.readerContent}>
                    {script.content || '(스크립트 내용이 없습니다)'}
                  </div>
                </article>
              )
            })}
          </main>
        </div>
      )}
    </div>
  )
}

export default CollectionView
